import { FormattedMessage, useIntl } from "react-intl";

import { ApiKeyRead } from "@nens/rana/api-v1";
import { RanaModal, RanaTable } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";

import { useIconSrc } from "@/hooks/use-icon-src";
import { Profile } from "@/lib/profile";
import { useShareKeyListQuery } from "@/queries/query-share-key";

import { getId, getIsPublishedVersion } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { PublicationDetailShareCreate } from "../share-create";

interface PublicationDetailShareListProps {
  onClose: () => void;
  setSelectedShare: (share: ApiKeyRead) => void;
}

export function PublicationDetailShareList(props: PublicationDetailShareListProps) {
  const intl = useIntl();
  const icon = useIconSrc("publication");
  const { onClose, setSelectedShare } = props;
  const isPublished = usePublicationStore(getIsPublishedVersion);

  const publicationId = usePublicationStore(getId);

  const { page, offset, setPageSize, limit, setPage } = useTableData({
    initialPageSize: 10,
  });
  const { data, isLoading } = useShareKeyListQuery({
    publicationId,
    offset,
    limit,
  });
  const tableDataIds = data?.items.map(({ id }) => id) ?? [];
  const numPages = Math.ceil((data?.total ?? 0) / limit);

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={<FormattedMessage id="heading.share_keys" defaultMessage="Share keys" />}
      maxW="fit"
      buttons={isPublished ? [<PublicationDetailShareCreate key="key" />] : undefined}
    >
      <RanaTable
        emptyElement={<RanaTable.Empty icon={icon} />}
        columnDefinitions={[
          {
            width: "table_cell_sm",
            title: intl.formatMessage({ id: "column.name", defaultMessage: "Name" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.created_at", defaultMessage: "Created at" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.created_by", defaultMessage: "Created by" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.expires_at", defaultMessage: "Expires at" }),
          },
          {
            width: "table_cell_sm",
            title: "",
          },
        ]}
        tableDataIds={tableDataIds}
        isSkeleton={isLoading}
      >
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {data?.items.map((share) => {
              const { id, name, createdAt, creator, expiresAt, revoked } = share;

              return (
                <RanaTable.Row
                  onClick={() => {
                    setSelectedShare(share);
                  }}
                  key={id}
                  rowId={id}
                  isDisabled={revoked}
                >
                  <RanaTable.Cell>{name}</RanaTable.Cell>
                  <RanaTable.Cell>{createdAt.toLocaleDateString()}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Profile inTable user={creator} />
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    {revoked ? (
                      <FormattedMessage id="heading.revoked" defaultMessage="Revoked" />
                    ) : (
                      expiresAt?.toLocaleDateString() ?? "-"
                    )}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
        <RanaTable.Pagination
          limit={limit}
          setPageSize={setPageSize}
          page={page}
          numPages={numPages}
          setPage={setPage}
        />
      </RanaTable>
    </RanaModal>
  );
}
